import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Link,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import api from '../services/api';

interface ArticleSummary {
  id: string;
  article_id: string;
  summary: string;
  relevance_score: number;
  created_at: string;
}

interface Article {
  id: string;
  source_id: string;
  url: string;
  title: string;
  publication_date: string | null;
  scraped_at: string;
  summary: ArticleSummary | null;
}

interface ArticlesResponse {
  items: Article[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const ArticlesTable: React.FC = () => {
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getWeekAgoDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState<'date' | 'score'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState<string>(getWeekAgoDate());
  const [dateTo, setDateTo] = useState<string>(getTodayDate());
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = (article: Article) => {
    setSelectedArticle(article);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedArticle(null);
  };

  // Fetch articles with React Query
  const { data, isLoading, isFetching, isPlaceholderData, isError, error } = useQuery({
    queryKey: ['articles', page + 1, rowsPerPage, sortBy, sortOrder, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const response = await api.get(`/api/articles?${params.toString()}`);
      return response.data.data as ArticlesResponse;
    },
    staleTime: 30000, // 30 seconds
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: 'date' | 'score') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const formatDate = (dateString: string | null, scrapedAt?: string) => {
    if (!dateString && scrapedAt) {
      // Si pas de date de publication, afficher la date de scraping
      const date = new Date(scrapedAt);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }) + ' (scrapé)';
    }
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'default';
  };

  const articles = data?.items || [];
  const total = data?.total || 0;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filtres
        </Typography>
        
        {/* Quick date filters */}
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Button
            size="small"
            variant={dateFrom === getTodayDate() && dateTo === getTodayDate() ? 'contained' : 'outlined'}
            onClick={() => {
              const today = getTodayDate();
              setDateFrom(today);
              setDateTo(today);
              setPage(0);
            }}
          >
            Aujourd'hui
          </Button>
          <Button
            size="small"
            variant={dateFrom === getWeekAgoDate() && dateTo === getTodayDate() ? 'contained' : 'outlined'}
            onClick={() => {
              setDateFrom(getWeekAgoDate());
              setDateTo(getTodayDate());
              setPage(0);
            }}
          >
            7 derniers jours
          </Button>
          <Button
            size="small"
            variant={(() => {
              const today = new Date();
              const monthAgo = new Date(today);
              monthAgo.setMonth(today.getMonth() - 1);
              return dateFrom === monthAgo.toISOString().split('T')[0] && dateTo === getTodayDate() ? 'contained' : 'outlined';
            })()}
            onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today);
              monthAgo.setMonth(today.getMonth() - 1);
              setDateFrom(monthAgo.toISOString().split('T')[0]);
              setDateTo(getTodayDate());
              setPage(0);
            }}
          >
            30 derniers jours
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setPage(0);
            }}
          >
            Toutes les dates
          </Button>
        </Box>

        {/* Date pickers */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Date de début"
            type="date"
            value={dateFrom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDateFrom(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Date de fin"
            type="date"
            value={dateTo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDateTo(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Chip
              label={`${total} article${total !== 1 ? 's' : ''}`}
              color={total > 0 ? 'primary' : 'default'}
              variant="outlined"
              size="small"
            />
            {isFetching && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Box>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des articles: {error instanceof Error ? error.message : 'Erreur inconnue'}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ position: 'relative', opacity: isPlaceholderData ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {(isLoading || (isFetching && isPlaceholderData)) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Titre</strong>
              </TableCell>
              <TableCell>
                <strong>URL</strong>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'date'}
                  direction={sortBy === 'date' ? sortOrder : 'desc'}
                  onClick={() => handleSort('date')}
                >
                  <strong>Date</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Tooltip title="Score de pertinence (0-100) : évalue l'intérêt de l'article pour LinkedIn et newsletters. 80+ = Excellent, 60-79 = Bon, 40-59 = Moyen, <40 = Faible">
                  <TableSortLabel
                    active={sortBy === 'score'}
                    direction={sortBy === 'score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('score')}
                  >
                    <strong>Score</strong>
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell>
                <strong>Synthèse</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    Aucun article trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {article.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Lien
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(article.publication_date, article.scraped_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {article.summary ? (
                      <Chip
                        label={article.summary.relevance_score}
                        color={getScoreColor(article.summary.relevance_score)}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {article.summary ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            flex: 1,
                          }}
                        >
                          {article.summary.summary}
                        </Typography>
                        <Tooltip title="Voir la synthèse complète">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(article)}
                            color="primary"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Synthèse non disponible
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Articles par page:"
          labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) => 
            `${from}-${to} sur ${count}`
          }
        />
      </TableContainer>

      {/* Dialog for full summary */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span">
              {selectedArticle?.title}
            </Typography>
            {selectedArticle?.summary && (
              <Chip
                label={`Score: ${selectedArticle.summary.relevance_score}/100`}
                color={getScoreColor(selectedArticle.summary.relevance_score)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Date: {formatDate(selectedArticle?.publication_date || null, selectedArticle?.scraped_at)}
            </Typography>
            <Link
              href={selectedArticle?.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
            >
              Voir l'article original
              <OpenInNewIcon fontSize="small" />
            </Link>
          </Box>
          
          <Typography variant="h6" gutterBottom>
            Synthèse
          </Typography>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              textAlign: 'justify',
            }}
          >
            {selectedArticle?.summary?.summary || 'Synthèse non disponible'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fermer</Button>
          <Button
            variant="contained"
            component="a"
            href={selectedArticle?.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
          >
            Lire l'article
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticlesTable;
